import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AdvancedSearchRequestDTO, SearchFieldMetadataDTO, FieldType } from '../../../types';
import AdvancedSearchField from './AdvancedSearchField';
import Icon from '@mdi/react';
import { mdiClose } from '@mdi/js';
import './AdvancedSearchPanel.scss';

interface AdvancedSearchPanelProps {
    isVisible: boolean;
    onClose: () => void;
    onSearch: (searchRequest: AdvancedSearchRequestDTO) => void;
    fieldMetadata: SearchFieldMetadataDTO[];
    initialValues?: AdvancedSearchRequestDTO;
}

const AdvancedSearchPanel: React.FC<AdvancedSearchPanelProps> = ({
    isVisible,
    onClose,
    onSearch,
    fieldMetadata,
    initialValues = {}
}) => {
    const [searchValues, setSearchValues] = useState<AdvancedSearchRequestDTO>(initialValues);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);

    // Debounce function
    function debounce<T extends any[]>(func: (...args: T) => void, delay: number): (...args: T) => void {
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        return (...args: T) => {
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
            }
            timeoutId = setTimeout(() => func(...args), delay);
        };
    }

    // Create a ref to hold the latest onSearch function
    const onSearchRef = useRef(onSearch);
    useEffect(() => {
        onSearchRef.current = onSearch;
    }, [onSearch]);

    // Update searchValues when initialValues change (for deep linking)
    useEffect(() => {
        if (initialValues && Object.keys(initialValues).length > 0) {
            console.log('AdvancedSearchPanel: Setting initial values:', initialValues);
            setSearchValues(initialValues);
            // Don't trigger automatic search for initial values from URL
            setHasUserInteracted(false);
        }
    }, [initialValues]);

    // Debounced search callback - memoized to prevent recreation
    const debouncedSearch = useCallback(
        debounce((values: AdvancedSearchRequestDTO) => {
            onSearchRef.current(values);
        }, 300),
        [] // Empty dependency array - using ref for onSearch
    );

    // Trigger search when values change (if user has interacted)
    useEffect(() => {
        if (hasUserInteracted) {
            debouncedSearch(searchValues);
        }
    }, [searchValues, debouncedSearch, hasUserInteracted]);

    const handleFieldChange = (fieldName: string, value: any) => {
        setHasUserInteracted(true);
        
        // Special handling for date range fields
        if (fieldName === 'dateCreated' && value) {
            setSearchValues(prev => ({
                ...prev,
                dateCreatedStart: value.start || undefined,
                dateCreatedEnd: value.end || undefined
            }));
        } else {
            setSearchValues(prev => ({
                ...prev,
                [fieldName]: value
            }));
        }
    };

    const handleClearAll = () => {
        setSearchValues({});
        setHasUserInteracted(true);
    };

    const handleClearField = (fieldName: string) => {
        setHasUserInteracted(true);
        setSearchValues(prev => {
            const newValues = { ...prev };
            
            // Special handling for date range fields
            if (fieldName === 'dateCreated') {
                delete newValues.dateCreatedStart;
                delete newValues.dateCreatedEnd;
            } else {
                delete newValues[fieldName as keyof AdvancedSearchRequestDTO];
            }
            
            return newValues;
        });
    };

    // Get all fields in a specific order for better layout
    const orderedFieldNames = [
        'firstName', 'lastName', 'businessName', 'toEmail', 'toPhone',
        'accountNumber', 'referralSource', 'accountType', 'status', 'type', 'appStatus',
        'externalSource', 'requestedBy', 'note',
        'dateCreated', 'id'
    ];

    const orderedFields = orderedFieldNames
        .map(name => fieldMetadata.find(f => f.fieldName === name))
        .filter(field => field !== undefined) as SearchFieldMetadataDTO[];

    return (
        <div className={`advanced-search-drawer ${isVisible ? 'expanded' : ''}`}>
            <div className="drawer-content">
                <div className="drawer-header">
                    <div className="header-left">
                        <span className="filter-count">
                            {Object.keys(searchValues).length > 0 
                                ? `${Object.keys(searchValues).length} filter${Object.keys(searchValues).length !== 1 ? 's' : ''} active`
                                : 'Advanced Search'
                            }
                        </span>
                    </div>
                    <div className="header-actions">
                        <button 
                            type="button" 
                            className="clear-all-btn"
                            onClick={handleClearAll}
                            disabled={Object.keys(searchValues).length === 0}
                        >
                            Clear All
                        </button>
                        <button 
                            type="button" 
                            className="close-btn" 
                            onClick={onClose}
                            aria-label="Close advanced search"
                        >
                            <Icon path={mdiClose} size={0.7} />
                        </button>
                    </div>
                </div>

                <div className="search-fields-grid">
                    {orderedFields.map(field => {
                        let fieldValue: any = searchValues[field.fieldName as keyof AdvancedSearchRequestDTO];
                        
                        // Special handling for dateCreated field
                        if (field.fieldName === 'dateCreated') {
                            fieldValue = {
                                start: searchValues.dateCreatedStart || '',
                                end: searchValues.dateCreatedEnd || ''
                            };
                        }
                        
                        return (
                            <AdvancedSearchField
                                key={field.fieldName}
                                field={field}
                                value={fieldValue}
                                onChange={(value) => handleFieldChange(field.fieldName, value)}
                                onClear={() => handleClearField(field.fieldName)}
                            />
                        );
                    })}
                </div>
            </div>

        </div>
    );
};

export default AdvancedSearchPanel;